import React from 'react';
import type { Migration, Resource } from '../../../types';
import type { TransformationFormData } from '../../ApproveExport/transformations/TransformationForm';
import type { Organization } from '../../../hooks/api/useFetchOrganizations';
import { Modal } from '../Shared/Modal';
import { SelectedResourcesTableHeader } from '../../resource/table/SelectedResourcesTableHeader';
import SelectedResourcesTableBody from '../../resource/table/SelectedResourcesTableBody';
import { useResourceSearch } from '../../../hooks/ui';
import { ui } from '../../../config/uiSizes';
import { toFriendlyName } from '../../../utils/formatters';
import { getTypeLabelByApiName } from '../../../utils/resourceTypes';
import { formatRegion } from '../../../utils/formatters';

// ── Per-transformation-type config renderer ─────────────────────────────────

/** Label + value row inside the config card */
const CfgRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="grid grid-cols-[10rem_1fr] gap-x-3 items-baseline">
    <span className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400`}>{label}</span>
    <span className={`${ui.text.xs} font-mono font-semibold text-gray-800 dark:text-gray-100 break-all`}>{value}</span>
  </div>
);

/** old → new arrow row inside the config card */
/** Renders each mapping entry as a labeled pair of CfgRows, matching form field labels */
const LabeledMappingList: React.FC<{
  entries: [string, string][];
  fromLabel: string;
  toLabel: string;
}> = ({ entries, fromLabel, toLabel }) => (
  <div className="space-y-2">
    {entries.map(([from, to], i) => (
      <div key={from} className="space-y-1.5">
        {i > 0 && <div className="border-t border-gray-100 dark:border-gray-700/60 pt-1" />}
        <CfgRow label={fromLabel} value={from} />
        <CfgRow label={toLabel} value={to} />
      </div>
    ))}
  </div>
);

const renderTransformationConfig = (
  transformationType: string,
  parameters: Record<string, unknown> | undefined
): React.ReactNode => {
  if (!parameters) return null;
  const p = parameters;

  switch (transformationType) {
    case 'AddPrefix':
    case 'RemovePrefix': {
      const prefix = p['prefix'] as string | undefined;
      return (
        <div className="space-y-1.5">
          {prefix && <CfgRow label="Prefix" value={prefix} />}
        </div>
      );
    }

    case 'ReplacePrefix': {
      const oldPrefix = p['oldPrefix'] as string | undefined;
      const newPrefix = p['newPrefix'] as string | undefined;
      return (
        <div className="space-y-1.5">
          {oldPrefix && <CfgRow label="Old prefix" value={oldPrefix} />}
          {newPrefix && <CfgRow label="New prefix" value={newPrefix} />}
        </div>
      );
    }

    case 'FlowLiteralReplace': {
      const find = p['find'] as string | undefined;
      const replace = p['replace'] as string | undefined;
      return (
        <div className="space-y-1.5">
          {find && <CfgRow label="Find text" value={find} />}
          {replace && <CfgRow label="Replace with" value={replace} />}
        </div>
      );
    }

    case 'RenameResource': {
      const mappings = p['mappings'];
      if (!mappings || typeof mappings !== 'object' || Array.isArray(mappings)) return null;
      const entries = Object.entries(mappings as Record<string, string>).filter(([, v]) => v);
      if (entries.length === 0) return null;
      return <LabeledMappingList entries={entries} fromLabel="Old name" toLabel="New name" />;
    }

    case 'DivisionMapping': {
      const dm = p['divisionMap'];
      let entries: [string, string][] = [];
      if (Array.isArray(dm)) {
        entries = (dm as Array<{ sourceDivision: string; targetDivision: string }>)
          .filter((r) => r.sourceDivision && r.targetDivision)
          .map((r) => [r.sourceDivision, r.targetDivision]);
      } else if (dm && typeof dm === 'object') {
        entries = Object.entries(dm as Record<string, string>).filter(([, v]) => v);
      }
      if (entries.length === 0) return null;
      return <LabeledMappingList entries={entries} fromLabel="Source division" toLabel="Target division" />;
    }

    case 'PropertyBatchUpdate': {
      const updates = p['propertyUpdates'];
      const onlyIfExists = p['onlyIfExists'] as boolean | undefined;
      const pairs: [string, string][] = Array.isArray(updates)
        ? (updates as Array<{ PropertyName?: string; NewValue?: string }>)
          .filter((u) => u.PropertyName && u.NewValue)
          .map((u) => [u.PropertyName ?? '', u.NewValue ?? ''])
        : [];
      return (
        <div className="space-y-1.5">
          {pairs.length > 0 && <LabeledMappingList entries={pairs} fromLabel="Property name" toLabel="New value" />}
          {onlyIfExists !== undefined && (
            <CfgRow label="Only if exists" value={onlyIfExists ? 'Yes' : 'No'} />
          )}
        </div>
      );
    }

    case 'EmailDomain': {
      const oldDomain = p['oldDomain'] as string | undefined;
      const newDomain = p['newDomain'] as string | undefined;
      const updatePrimary = p['updatePrimaryEmail'] as boolean | undefined;
      const updateAlt = p['updateAlternateEmails'] as boolean | undefined;
      const preservePrefix = p['preservePrefix'] as boolean | undefined;
      return (
        <div className="space-y-1.5">
          {oldDomain && <CfgRow label="Old domain" value={oldDomain} />}
          {newDomain && <CfgRow label="New domain" value={newDomain} />}
          {updatePrimary !== undefined && <CfgRow label="Update primary email" value={updatePrimary ? 'Yes' : 'No'} />}
          {updateAlt !== undefined && <CfgRow label="Update alternate emails" value={updateAlt ? 'Yes' : 'No'} />}
          {preservePrefix !== undefined && <CfgRow label="Preserve prefix" value={preservePrefix ? 'Yes' : 'No'} />}
        </div>
      );
    }

    case 'OverwriteProperty': {
      const propertyName = p['propertyName'] as string | undefined;
      const newValue = p['newValue'] as string | undefined;
      const createIfMissing = p['createIfMissing'] as boolean | undefined;
      const expectedType = p['expectedType'] as string | undefined;
      return (
        <div className="space-y-1.5">
          {propertyName && <CfgRow label="Property" value={propertyName} />}
          {newValue !== undefined && newValue !== '' && <CfgRow label="New value" value={String(newValue)} />}
          {createIfMissing !== undefined && <CfgRow label="Create if missing" value={createIfMissing ? 'Yes' : 'No'} />}
          {expectedType && <CfgRow label="Expected type" value={expectedType} />}
        </div>
      );
    }

    case 'ExcludeResource': {
      const exclusionType = p['exclusionType'] as string | undefined;
      const namePattern = p['namePattern'] as string | undefined;
      const resourceIds = p['resourceIds'];
      const propertyPath = p['propertyPath'] as string | undefined;
      const propertyValue = p['propertyValue'] as string | undefined;
      const caseSensitive = p['caseSensitive'] as boolean | undefined;
      return (
        <div className="space-y-1.5">
          {exclusionType && <CfgRow label="Exclusion type" value={exclusionType} />}
          {namePattern && <CfgRow label="Name pattern" value={namePattern} />}
          {Array.isArray(resourceIds) && resourceIds.length > 0 && (
            <CfgRow label="Resource IDs" value={(resourceIds as string[]).join(', ')} />
          )}
          {propertyPath && <CfgRow label="Property path" value={propertyPath} />}
          {propertyValue && <CfgRow label="Property value" value={propertyValue} />}
          {caseSensitive !== undefined && (
            <CfgRow label="Case sensitive" value={caseSensitive ? 'Yes' : 'No'} />
          )}
        </div>
      );
    }

    case 'DeleteInvalidData': {
      const flags: [string, boolean | undefined][] = [
        ['Remove null values', p['removeNullValues'] as boolean | undefined],
        ['Remove empty strings', p['removeEmptyStrings'] as boolean | undefined],
        ['Remove empty arrays', p['removeEmptyArrays'] as boolean | undefined],
        ['Remove empty objects', p['removeEmptyObjects'] as boolean | undefined],
      ];
      const invalidProps = p['removeInvalidProperties'];
      return (
        <div className="space-y-1.5">
          {flags.filter(([, v]) => v !== undefined).map(([label, val]) => (
            <CfgRow key={label} label={label} value={val ? 'Yes' : 'No'} />
          ))}
          {Array.isArray(invalidProps) && invalidProps.length > 0 && (
            <CfgRow label="Remove properties" value={(invalidProps as string[]).join(', ')} />
          )}
        </div>
      );
    }

    case 'ResourceFilter': {
      const excludeDivisions = p['excludeDivisions'];
      const excludePatterns = p['excludePatterns'];
      const includePatterns = p['includePatterns'];
      const excludeResourceTypes = p['excludeResourceTypes'];
      return (
        <div className="space-y-1.5">
          {Array.isArray(excludeDivisions) && excludeDivisions.length > 0 && (
            <CfgRow label="Exclude divisions" value={(excludeDivisions as string[]).join(', ')} />
          )}
          {Array.isArray(excludePatterns) && excludePatterns.length > 0 && (
            <CfgRow label="Exclude patterns" value={(excludePatterns as string[]).join(', ')} />
          )}
          {Array.isArray(includePatterns) && includePatterns.length > 0 && (
            <CfgRow label="Include patterns" value={(includePatterns as string[]).join(', ')} />
          )}
          {Array.isArray(excludeResourceTypes) && excludeResourceTypes.length > 0 && (
            <CfgRow label="Exclude types" value={(excludeResourceTypes as string[]).join(', ')} />
          )}
        </div>
      );
    }

    default:
      return null;
  }
};

// ── Accordion list of transformations ────────────────────────────────────────

interface TransformationAccordionListProps {
  transformations: TransformationFormData[];
  transformationTypes: Array<{ value: string; label: string; description: string }>;
}

const TransformationAccordionList: React.FC<TransformationAccordionListProps> = ({
  transformations,
  transformationTypes
}) => {
  const [openIds, setOpenIds] = React.useState<Set<string>>(() => new Set([transformations[0]?.id ?? '']));

  const toggle = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {transformations.map((t) => {
        const typeMeta = transformationTypes.find((tt) => tt.value === t.transformationType);
        const isOpen = openIds.has(t.id);

        return (
          <div key={t.id} className="bg-white dark:bg-gray-800">
            {/* Card header — click to expand/collapse */}
            <button
              type="button"
              onClick={() => toggle(t.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              {/* Chevron */}
              <svg
                className={`${ui.svg.sm} text-gray-400 dark:text-gray-500 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-90' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>

              {/* Title only — no description */}
              <div className="flex-1 min-w-0">
                <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100 leading-tight`}>
                  {typeMeta?.label ?? t.transformationType}
                </div>
              </div>
            </button>

            {/* Collapsible body */}
            {isOpen && (
              <div className="px-4 pb-4 pt-3 space-y-3 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-100 dark:border-gray-700/60">
                {/* What it does row */}
                {typeMeta?.description && (
                  <div className="flex items-start gap-2">
                    <span className={`${ui.text.xs} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide shrink-0 pt-0.5 w-32`}>
                      What it does
                    </span>
                    <span className={`${ui.text.label} text-gray-700 dark:text-gray-300`}>
                      {typeMeta.description}
                    </span>
                  </div>
                )}
                {/* Resource Types row */}
                <div className="flex items-start gap-2">
                  <span className={`${ui.text.xs} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide shrink-0 pt-0.5 w-32`}>
                    Resource Types
                  </span>
                  {t.resourceTypes.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {t.resourceTypes.map((rt) => (
                        <span
                          key={rt}
                          className={`${ui.badge.sm} bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 rounded ${ui.text.xs} font-medium`}
                        >
                          {getTypeLabelByApiName(toFriendlyName(rt))}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className={`${ui.text.label} text-gray-400 dark:text-gray-500 italic`}>
                      All resource types
                    </span>
                  )}
                </div>

                {/* Per-type configuration summary — distinct inset card */}
                {(() => {
                  const configContent = renderTransformationConfig(
                    t.transformationType,
                    t.transformation.parameters
                  );
                  if (!configContent) return null;
                  return (
                    <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                      {/* Card header strip */}
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-600">
                        <svg className="w-3 h-3 text-gray-400 dark:text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className={`${ui.text.xs} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide`}>
                          Configuration
                        </span>
                      </div>
                      {/* Card body */}
                      <div className="px-3 py-2.5 bg-white dark:bg-gray-800/50 space-y-1.5">
                        {configContent}
                      </div>
                    </div>
                  );
                })()}


              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface ConfirmApproveExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onNoTransformConfirm?: () => void;
  /** Skip-transformations preview: queue the import as a plan-only dry run (nothing applied). */
  onPreviewNoTransform?: () => void;
  job: Migration | undefined;
  resources: Resource[];
  transformations: TransformationFormData[];
  transformationTypes: Array<{ value: string; label: string; description: string }>;
  confirmLoading: boolean;
  previewLoading?: boolean;
  isRerun?: boolean;
  isEditMode?: boolean;
  organizations?: Organization[];
  isD2D?: boolean;
  d2dSourceDivisionName?: string;
  d2dTargetDivisionName?: string;
}

export const ConfirmApproveExportModal: React.FC<ConfirmApproveExportModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onNoTransformConfirm,
  onPreviewNoTransform,
  job,
  resources,
  transformations,
  transformationTypes,
  confirmLoading,
  previewLoading = false,
  isRerun = false,
  isEditMode = false,
  organizations = [],
  isD2D = false,
  d2dSourceDivisionName,
  d2dTargetDivisionName
}) => {
  const [typeSearch, setTypeSearch] = React.useState('');
  const [nameSearch, setNameSearch] = React.useState('');

  const { filteredResourcesByType } = useResourceSearch(resources, typeSearch, nameSearch);

  const filteredResourceTypes = React.useMemo(
    () => Array.from(filteredResourcesByType.keys()),
    [filteredResourcesByType]
  );

  const hasTransformations = transformations.length > 0;
  // For D2D migrations the DivisionMapping is auto-applied, so treat the confirmation
  // flow identically to a migration where the user added a transformation themselves.
  const effectivelyHasTransformations = hasTransformations || isD2D;

  // Helper function for button label
  const getConfirmLabel = (isEdit: boolean, isRerun: boolean, hasTransforms: boolean, loading: boolean): string => {
    // When loading, show consistent loading text regardless of mode
    if (loading) {
      return hasTransforms ? 'Starting Transformation...' : 'Starting Import...';
    }

    // Non-loading state - show appropriate action text
    if (isEdit) {
      return hasTransforms ? 'Update & Rerun Transformation' : 'Update & Start Import';
    }
    if (isRerun) {
      return hasTransforms ? 'Redo Transformation' : 'Redo Import';
    }
    return hasTransforms ? 'Start Transformation' : 'Start Import';
  };

  const confirmLabel = getConfirmLabel(isEditMode, isRerun, effectivelyHasTransformations, confirmLoading);

  const handlePrimaryAction = () => {
    if (!effectivelyHasTransformations && onNoTransformConfirm) {
      onNoTransformConfirm();
    } else {
      onConfirm();
    }
  };

  // Reset search when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setTypeSearch('');
      setNameSearch('');
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      variant="runMigration"
      title="Confirm Export Approval"
      onClose={onClose}
      showConfirmButton={false}
      showIcon
      showCloseButton={!confirmLoading}
      customButtons={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={confirmLoading}
            className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-gray-700 dark:text-gray-300 font-semibold shadow transition hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          {!effectivelyHasTransformations && !isEditMode && !isRerun && onPreviewNoTransform && (
            <button
              type="button"
              onClick={onPreviewNoTransform}
              disabled={confirmLoading || previewLoading}
              title="Run a Terraform plan-only preview — nothing is applied to the target org."
              className="flex-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600 px-4 py-2 text-white font-semibold shadow transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {previewLoading && (
                <svg className={`animate-spin ${ui.svg.sm}`} viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
              {previewLoading ? 'Starting Preview...' : 'Preview (dry run)'}
            </button>
          )}
          <button
            type="button"
            onClick={handlePrimaryAction}
            disabled={confirmLoading}
            className="flex-1 rounded-lg bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 px-4 py-2 text-white font-semibold shadow transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {confirmLoading && (
              <svg className={`animate-spin ${ui.svg.sm}`} viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="w-full space-y-3">
        {/* Migration name */}
        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex-shrink-0">
            <svg
              className="w-4 h-4 text-orange-600 dark:text-orange-400"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider`}>
              Migration Name
            </div>
            <div
              className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100 break-words`}
            >
              {job?.name ?? '[Name]'}
            </div>
          </div>
        </div>

        {/* Edit mode warning - always show when editing */}
        {isEditMode && (
          <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
            <svg
              className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <p className={`${ui.text.label} text-yellow-800 dark:text-yellow-200`}>
              <strong>Warning:</strong> This will{' '}
              {hasTransformations ? (
                <>
                  <strong>replace the existing transformations</strong> and rerun the transformation
                  process with your updated configuration.
                </>
              ) : (
                <>
                  <strong>remove all existing transformations</strong> and skip directly to import.
                </>
              )}
            </p>
          </div>
        )}
        {/* D2D auto-applied Division Mapping note */}
        {isD2D && (
          <div className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-700">
            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className={`${ui.text.label} font-semibold text-emerald-800 dark:text-emerald-300`}>
                Division Mapping will be automatically applied
              </p>
              {d2dSourceDivisionName && d2dTargetDivisionName && (
                <table className={`mt-2 border-collapse ${ui.text.xs} text-emerald-700 dark:text-emerald-400`}>
                  <tbody>
                    <tr>
                      <td className="pr-4 py-0.5 font-medium whitespace-nowrap">Source division</td>
                      <td className="py-0.5">{d2dSourceDivisionName}</td>
                    </tr>
                    <tr>
                      <td className="pr-4 py-0.5 font-medium whitespace-nowrap">Target division</td>
                      <td className="py-0.5">{d2dTargetDivisionName}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* No-transformations notice — shown at top so it's immediately visible */}
        {!hasTransformations && (
          <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <svg
              className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p className={`${ui.text.label} text-blue-800 dark:text-blue-200`}>
              {isD2D ? (
                <>
                  <strong>No additional transformations configured.</strong> The Division Mapping transformation will run automatically to remap division references before import. You will review the results before import begins.
                </>
              ) : (
                <>
                  <strong>No transformations configured.</strong> The export will be approved and import will begin immediately.
                </>
              )}
            </p>
          </div>
        )}

        {/* Source → Target Org flow card */}
        <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-800/30 p-4 shadow-sm">
          <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-4">
            {/* Source */}
            <div className="flex items-start gap-3 min-w-0">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg shrink-0">
                <svg className="w-4 h-4 text-teal-600 dark:text-teal-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`${ui.text.xs} font-semibold text-teal-700 dark:text-teal-400 mb-1 uppercase tracking-wider`}>
                  Source Org
                </div>
                <div className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>
                  {job?.sourceOrgName ? (() => {
                    const org = organizations.find(o => o.name === job.sourceOrgName || o.shortName === job.sourceOrgName);
                    return `${org?.name ?? job.sourceOrgName} - ${formatRegion(org?.region)}`;
                  })() : '[Org]'}
                </div>
              </div>
            </div>
            {/* Arrow */}
            <div className="flex items-center justify-center self-center">
              <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
            {/* Target */}
            <div className="flex items-start gap-3 min-w-0">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg shrink-0">
                <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`${ui.text.xs} font-semibold text-indigo-700 dark:text-indigo-400 mb-1 uppercase tracking-wider`}>
                  Target Org
                </div>
                <div className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug`}>
                  {job?.targetOrgName ? (() => {
                    const org = organizations.find(o => o.name === job.targetOrgName || o.shortName === job.targetOrgName);
                    return `${org?.name ?? job.targetOrgName} - ${formatRegion(org?.region)}`;
                  })() : '[Org]'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resource count tile */}
        <div className="relative overflow-hidden rounded-xl border border-blue-200 dark:border-blue-700/60 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/10 p-5 shadow-sm">
          <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-blue-400 to-indigo-500" />
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-800/60 rounded-md ring-1 ring-blue-200 dark:ring-blue-700">
              <svg className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`} fill="currentColor" viewBox="0 0 20 20">
                <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
              </svg>
            </div>
            <p className={`${ui.text.xs} uppercase tracking-wider font-semibold text-blue-700 dark:text-blue-400`}>Resources</p>
          </div>
          <p className="text-5xl font-extrabold text-gray-900 dark:text-gray-100 leading-none mb-2 tabular-nums">
            {resources.length}
          </p>
          <div className="h-px bg-blue-200/70 dark:bg-blue-700/40 my-2" />
          <p className={`${ui.text.xs} text-gray-600 dark:text-gray-400 leading-snug`}>
            {resources.length === 1 ? 'resource will be migrated' : 'resources will be migrated'} to the target organization
          </p>
        </div>

        {/* Exported resources by type */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <SelectedResourcesTableHeader
            typeSearch={typeSearch}
            nameSearch={nameSearch}
            onSetTypeSearchChange={setTypeSearch}
            onSetNameSearchChange={setNameSearch}
          />
          <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto">
            <SelectedResourcesTableBody
              filteredResourcesByType={filteredResourcesByType}
              filteredResourceTypes={filteredResourceTypes}
              typeSearch={typeSearch}
              nameSearch={nameSearch}
              isFullSnapshotMode={false}
              emptyMessage="No exported resources found."
            />
          </div>
        </div>

        {/* Transformations summary */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h4 className={`${ui.text.label} font-bold text-gray-900 dark:text-gray-100`}>
              Transformations
            </h4>
            {hasTransformations && (
              <span className={`${ui.badge.sm} bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full font-bold ${ui.text.xs}`}>
                {transformations.length}
              </span>
            )}
          </div>

          {!hasTransformations ? (
            <div className={`px-4 py-3 ${ui.text.label} text-gray-400 dark:text-gray-500 italic`}>
              None
            </div>
          ) : (
            <TransformationAccordionList
              transformations={transformations}
              transformationTypes={transformationTypes}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmApproveExportModal;
